import type { Dispatch, KeyboardEvent, SetStateAction } from 'react';
import { useCallback, useState } from 'react';
import { getHandleActionKeyDown } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { MenuPosition } from '../../../../../types';
import { usePopupContext } from '../../../../PopupProvider';
import { useData } from '../main-layouts';
import { ItemLayoutOne } from './ItemLayoutOne';
import { ItemLayoutThree } from './ItemLayoutThree';
import { ItemLayoutTwo } from './ItemLayoutTwo';

const layouts = {
  1: ItemLayoutOne,
  2: ItemLayoutTwo,
  3: ItemLayoutThree
};

export interface SelectedItemLayout {
  data: MenuPosition;
  width: number;
  priceWidth: number;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<Element>) => void;
  setPriceWidth: Dispatch<SetStateAction<number>>;
  showImage?: boolean;
}

interface ItemLayoutProps {
  data: MenuPosition;
  width?: number;
  showImage?: boolean;
}

export function ItemLayout({
  data,
  width = 100,
  showImage = true
}: ItemLayoutProps) {
  const [priceWidth, setPriceWidth] = useState(0);
  const {
    visibleSections,
    itemsBySections,
    metadata: { sectionsEnabled }
  } = useData();
  const { itemLayout, popupEnabled } = useSettings();

  const {
    showPopup,
    setPopupItem: setPopupItemFromContext,
    setPopupCurrentItemSection,
    setPopupSections
  } = usePopupContext();
  const { section } = data;

  const setPopupItem = useCallback(() => {
    if (!popupEnabled) {
      return;
    }

    const isSectionUncategorized = !sectionsEnabled || !section;

    showPopup();
    setPopupItemFromContext(data);
    setPopupSections(visibleSections);

    if (isSectionUncategorized) {
      setPopupCurrentItemSection(itemsBySections['uncategorized']);
      return;
    }

    setPopupCurrentItemSection(itemsBySections[section]);
  }, [
    itemsBySections,
    section,
    popupEnabled,
    sectionsEnabled,
    showPopup,
    setPopupItemFromContext,
    data,
    setPopupCurrentItemSection,
    setPopupSections,
    visibleSections
  ]);

  const Layout = layouts[itemLayout];

  return (
    <Layout
      data={data}
      width={width}
      onClick={setPopupItem}
      onKeyDown={getHandleActionKeyDown(setPopupItem)}
      showImage={showImage}
      priceWidth={priceWidth}
      setPriceWidth={setPriceWidth}
    />
  );
}
